import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Skeleton,
  Alert,
  Tooltip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  FavoriteBorderOutlined,
  FavoriteOutlined,
  ShareOutlined,
  ShoppingCartOutlined,
  CheckCircle,
  LocalShipping,
  Replay,
  AccessTime,
  NavigateBefore,
  VerifiedOutlined,
  ZoomIn,
  ZoomOut,
  StorefrontOutlined,
  LocationOnOutlined,
  StarOutlined,
} from "@mui/icons-material";
// Fix the import path - adjust based on your actual file structure
import {
  productDetails,
  MultipleVendorforProduct,
} from "../../../services/api/Product";
import SellerSection from "../../../pages/SellerSection/SellerSection";
import VariantSelector from "../../../pages/VariantSelector/VariantSelector";
import { useDispatch, useSelector } from "react-redux";
import { AddtoCart } from "../../../services/api/cartService";

// Color constants
const T = {
  navy: "#0a1628",
  blue: "#1a5aff",
  blueSoft: "#e8efff",
  success: "#00c896",
  surface: "#f7f8fb",
  border: "#e4e8f0",
  muted: "#8898b0",
  white: "#ffffff",
  gradBlue: "linear-gradient(135deg, #1a5aff 0%, #0d3acc 100%)",
};

// Global styles
const GLOBAL_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');

  @keyframes pd-fadeUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes pd-cartPop {
    0%,100% { transform:scale(1); }
    45%     { transform:scale(1.07); }
  }
  @keyframes pd-shimmer {
    0%   { background-position:-600px 0; }
    100% { background-position: 600px 0; }
  }
  @keyframes pd-slideIn {
    from { opacity:0; transform:translateX(-10px); }
    to   { opacity:1; transform:translateX(0); }
  }

  .pd-root * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
  .pd-hero-img { transition:transform .35s cubic-bezier(.25,.8,.25,1); display:block; }
  .pd-thumb { transition:all .2s cubic-bezier(0.4,0,0.2,1); cursor:pointer; position:relative; overflow:hidden; }
  .pd-thumb:hover { transform:translateX(3px); }
  .pd-spec { transition:all .18s ease; }
  .pd-spec:hover { transform:translateY(-2px); box-shadow:0 6px 18px rgba(26,90,255,.09); }
  .pd-pill { transition:all .16s ease; cursor:pointer; }
  .pd-pill:hover { transform:translateY(-1px); }
  .pd-a1 { animation:pd-fadeUp .4s ease both .06s; }
  .pd-a2 { animation:pd-fadeUp .4s ease both .13s; }
  .pd-a3 { animation:pd-fadeUp .4s ease both .20s; }
  .pd-a4 { animation:pd-fadeUp .4s ease both .27s; }
  .pd-a5 { animation:pd-fadeUp .4s ease both .34s; }
  .pd-thumb-slide { animation:pd-slideIn .3s ease both; }
`;

const injectStyles = () => {
  if (document.getElementById("pd-global")) return;
  const el = document.createElement("style");
  el.id = "pd-global";
  // el.textContent = GLOBAL_STYLE;
  document.head.appendChild(el);
};

// Layout constants
const leftPanelSx = {
  flex: "0 0 50%",
  width: "50%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  p: { xs: 2, md: 3 },
  overflow: "hidden",
  bgcolor: T.surface,
  borderRight: `1px solid ${T.border}`,
  height: "100%",
  boxSizing: "border-box",
};

const rightPanelSx = {
  flex: "0 0 50%",
  width: "50%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  p: { xs: 2, md: 3 },
  overflowY: "auto",
  overflowX: "hidden",
  bgcolor: T.white,
  height: "100%",
  boxSizing: "border-box",
  scrollBehavior: "smooth",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: T.surface,
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: T.border,
    borderRadius: "10px",
    "&:hover": {
      background: T.muted,
    },
  },
};

// Helper Components
const TopBar = ({ onClose, label }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 2,
      py: 1.25,
      borderBottom: `1px solid ${T.border}`,
      bgcolor: T.white,
      flexShrink: 0,
    }}
  >
    <IconButton
      size="small"
      onClick={onClose}
      sx={{
        color: T.navy,
        bgcolor: T.surface,
        borderRadius: 2,
        "&:hover": { bgcolor: T.blueSoft, color: T.blue },
      }}
    >
      <NavigateBefore />
    </IconButton>
    <Typography
      sx={{
        fontFamily: "'Syne',sans-serif",
        fontSize: 13.5,
        fontWeight: 700,
        color: T.navy,
        letterSpacing: "-0.01em",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const fabSx = {
  bgcolor: T.white,
  border: `1px solid ${T.border}`,
  boxShadow: "0 2px 8px rgba(10,22,40,.08)",
  "&:hover": { bgcolor: "#f7f8fb" },
};

const Divider = () => (
  <Box sx={{ height: "1px", bgcolor: T.border, flexShrink: 0 }} />
);

const Label = ({ children }) => (
  <Typography
    sx={{
      fontSize: 10.5,
      fontWeight: 700,
      color: T.muted,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    }}
  >
    {children}
  </Typography>
);

const SpecCard = ({ label, value }) => (
  <Box
    className="pd-spec"
    sx={{
      bgcolor: T.surface,
      borderRadius: 2.5,
      px: 1.4,
      py: 1.1,
      border: `1px solid ${T.border}`,
    }}
  >
    <Typography
      sx={{
        fontSize: 9.5,
        color: T.muted,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        mb: 0.3,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: 12.5, fontWeight: 700, color: T.navy, lineHeight: 1.3 }}
    >
      {value}
    </Typography>
  </Box>
);

const badgePalette = {
  success: { bg: "#e8faf4", color: "#00865a", border: "#a7f3d0" },
  info: { bg: "#e8efff", color: "#1a5aff", border: "#bfdbfe" },
  warning: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
};

const Badge = ({ color, icon, children }) => {
  const c = badgePalette[color] || badgePalette.info;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.4,
        py: 0.55,
        borderRadius: 99,
        bgcolor: c.bg,
        border: `1px solid ${c.border}`,
        color: c.color,
        fontSize: 11.5,
        fontWeight: 700,
      }}
    >
      {icon}
      {children}
    </Box>
  );
};

// Helper function to get final price from vendor or variant
const getFinalPrice = (vendorOrVariant) => {
  if (vendorOrVariant?.finalPrice) {
    return vendorOrVariant.finalPrice;
  }
  if (vendorOrVariant?.salePrice) {
    return vendorOrVariant.salePrice;
  }
  if (vendorOrVariant?.variantSalePrice) {
    return vendorOrVariant.variantSalePrice;
  }
  return 0;
};

// Image Gallery Component
const ImageGallery = React.memo(
  ({
    images,
    activeIdx,
    activeImage,
    isZoomed,
    zoomPosition,
    isFavorite,
    onImageSwitch,
    onZoomToggle,
    onMouseMove,
    onFavoriteToggle,
  }) => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flex: 1,
          minHeight: 0,
          alignItems: "flex-start",
        }}
      >
        {/* Thumbnail strip */}
        {images?.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              flexShrink: 0,
              width: 80,
              maxHeight: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: "3px" },
              "&::-webkit-scrollbar-track": {
                background: T.surface,
                borderRadius: 10,
              },
              "&::-webkit-scrollbar-thumb": {
                background: T.border,
                borderRadius: 10,
              },
            }}
          >
            {images.map((img, i) => (
              <Box
                key={img.id || i}
                className={`pd-thumb ${activeIdx === i ? "pd-thumb-slide" : ""}`}
                onClick={() => onImageSwitch(img.url, i)}
                sx={{
                  width: 70,
                  height: 70,
                  flexShrink: 0,
                  borderRadius: 2,
                  border: `2px solid ${activeIdx === i ? T.blue : T.border}`,
                  boxShadow:
                    activeIdx === i
                      ? "0 0 0 3px rgba(26,90,255,.15), 0 4px 12px rgba(0,0,0,.08)"
                      : "none",
                  bgcolor: T.white,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 0.5,
                  position: "relative",
                  transition: "all 0.2s ease",
                }}
              >
                <Box
                  component="img"
                  src={img.url}
                  alt={`Thumbnail ${i + 1}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
                {activeIdx === i && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      bgcolor: T.blue,
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Main image */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            height: 350,
            borderRadius: 3,
            border: `1px solid ${T.border}`,
            bgcolor: T.white,
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(10,22,40,.07)",
            cursor: isZoomed ? "zoom-out" : "zoom-in",
            flexShrink: 0,
          }}
          onMouseMove={onMouseMove}
          onClick={onZoomToggle}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {activeImage ? (
              <Box
                component="img"
                className="pd-hero-img"
                src={activeImage}
                alt="Product"
                sx={{
                  width: isZoomed ? "200%" : "100%",
                  height: isZoomed ? "200%" : "100%",
                  objectFit: isZoomed ? "none" : "contain",
                  objectPosition: isZoomed
                    ? `${zoomPosition.x}% ${zoomPosition.y}%`
                    : "center",
                  display: "block",
                  transition: isZoomed ? "none" : "all 0.3s ease",
                }}
              />
            ) : (
              <Typography sx={{ fontSize: 72, lineHeight: 1 }}>📦</Typography>
            )}
          </Box>

          {/* Zoom badge */}
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              left: 8,
              px: 1,
              py: 0.5,
              borderRadius: 99,
              bgcolor: "rgba(10,22,40,.65)",
              color: "#fff",
              fontSize: 10,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backdropFilter: "blur(4px)",
              pointerEvents: "none",
            }}
          >
            {isZoomed ? (
              <>
                <ZoomOut sx={{ fontSize: 12 }} />
                Click to zoom out
              </>
            ) : (
              <>
                <ZoomIn sx={{ fontSize: 12 }} />
                Click to zoom
              </>
            )}
          </Box>

          {/* Action FABs */}
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              zIndex: 2,
            }}
          >
            <Tooltip
              title={isFavorite ? "Remove wishlist" : "Add to wishlist"}
              placement="left"
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onFavoriteToggle();
                }}
                sx={fabSx}
              >
                {isFavorite ? (
                  <FavoriteOutlined sx={{ fontSize: 17, color: "#e53935" }} />
                ) : (
                  <FavoriteBorderOutlined
                    sx={{ fontSize: 17, color: T.muted }}
                  />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Share" placement="left">
              <IconButton
                size="small"
                onClick={(e) => e.stopPropagation()}
                sx={fabSx}
              >
                <ShareOutlined sx={{ fontSize: 17, color: T.muted }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Image counter */}
          {images?.length > 1 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 8,
                right: 10,
                px: 1.2,
                py: 0.3,
                borderRadius: 99,
                bgcolor: "rgba(10,22,40,.65)",
                color: "#fff",
                fontSize: 10.5,
                fontWeight: 700,
                backdropFilter: "blur(4px)",
                zIndex: 2,
                pointerEvents: "none",
              }}
            >
              {activeIdx + 1} / {images.length}
            </Box>
          )}

          {/* Prev / Next arrows */}
          {images?.length > 1 && (
            <>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  const prev =
                    activeIdx === 0 ? images.length - 1 : activeIdx - 1;
                  onImageSwitch(images[prev].url, prev);
                }}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(255,255,255,.9)",
                  backdropFilter: "blur(4px)",
                  width: 32,
                  height: 32,
                  zIndex: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,.1)",
                  "&:hover": { bgcolor: T.white },
                }}
              >
                <NavigateBefore />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  const next =
                    activeIdx === images.length - 1 ? 0 : activeIdx + 1;
                  onImageSwitch(images[next].url, next);
                }}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%) rotate(180deg)",
                  bgcolor: "rgba(255,255,255,.9)",
                  backdropFilter: "blur(4px)",
                  width: 32,
                  height: 32,
                  zIndex: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,.1)",
                  "&:hover": { bgcolor: T.white },
                }}
              >
                <NavigateBefore />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
    );
  },
);

// Vendor Selection Component
const VendorSelector = ({ vendors, selectedVendor, onVendorSelect }) => {
  return (
    <Box>
      <Label>Select Seller</Label>
      <FormControl component="fieldset" sx={{ width: "100%", mt: 1 }}>
        <RadioGroup
          value={selectedVendor?.vendorId || ""}
          onChange={(e) => {
            const vendor = vendors.find((v) => v.vendorId === e.target.value);
            onVendorSelect(vendor);
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {vendors.map((vendor) => (
              <Paper
                key={vendor.vendorId}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `2px solid ${selectedVendor?.vendorId === vendor.vendorId ? T.blue : T.border}`,
                  bgcolor:
                    selectedVendor?.vendorId === vendor.vendorId
                      ? T.blueSoft
                      : T.white,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: T.blue,
                    bgcolor: T.blueSoft,
                  },
                }}
              >
                <FormControlLabel
                  value={vendor.vendorId}
                  control={
                    <Radio
                      sx={{
                        color: T.muted,
                        "&.Mui-checked": { color: T.blue },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 700, color: T.navy, fontSize: 14 }}
                        >
                          {vendor.vendorName}
                        </Typography>
                        {vendor.averageRating > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <StarOutlined
                              sx={{ fontSize: 14, color: "#ffc107" }}
                            />
                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                              {vendor.averageRating}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <LocationOnOutlined
                          sx={{ fontSize: 12, color: T.muted }}
                        />
                        <Typography sx={{ fontSize: 11, color: T.muted }}>
                          {vendor.address?.split(",")[0] ||
                            "Address not available"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 1,
                        }}
                      >
                        <Box>
                          {vendor.discount > 0 && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 11,
                                color: T.success,
                                fontWeight: 700,
                                bgcolor: "#e8faf4",
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                mr: 1,
                              }}
                            >
                              {vendor.discount}% OFF
                            </Typography>
                          )}
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 11,
                              color: vendor.isAvailableNow
                                ? T.success
                                : "#f44336",
                              fontWeight: 600,
                            }}
                          >
                            {vendor.isAvailableNow
                              ? "In Stock"
                              : "Out of Stock"}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            sx={{
                              fontSize: 18,
                              fontWeight: 800,
                              color: T.navy,
                              lineHeight: 1,
                            }}
                          >
                            ₹{getFinalPrice(vendor)}
                          </Typography>
                          {vendor.mrp > vendor.salePrice && (
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: T.muted,
                                textDecoration: "line-through",
                              }}
                            >
                              ₹{vendor.mrp}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  }
                  sx={{
                    alignItems: "flex-start",
                    width: "100%",
                    margin: 0,
                    "& .MuiFormControlLabel-label": { width: "100%" },
                  }}
                />
              </Paper>
            ))}
          </Box>
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

// Updated Product Info Panel with modular components
const ProductInfoPanel = React.memo(
  ({
    product,
    vendors,
    selectedVendor,
    selectedVendorVariant,
    onVendorSelect,
    onVariantSelect,
    isFavorite,
    onFavoriteToggle,
    addedToCart,
    onAddToCart,
    postalCode, // Add postal code prop
    isLoading = false,
  }) => {
    const returnPolicy = product?.attributes?.find(
      (a) => a.name === "Return Policy",
    )?.value;
    const specs =
      product?.attributes?.filter((a) => a.name !== "Return Policy") || [];

    // Get current display price
    const currentPrice = useMemo(() => {
      if (selectedVendorVariant) {
        return (
          selectedVendorVariant.finalPrice || selectedVendorVariant.salePrice
        );
      }
      if (selectedVendor) {
        return getFinalPrice(selectedVendor);
      }
      return 0;
    }, [selectedVendor, selectedVendorVariant]);

    console.log("Current Price:", currentPrice);

    const currentMrp = useMemo(() => {
      if (selectedVendorVariant?.variantMrp)
        return selectedVendorVariant.variantMrp;
      if (selectedVendor?.mrp) return selectedVendor.mrp;
      return 0;
    }, [selectedVendor, selectedVendorVariant]);

    // Get current vendor variants
    const currentVariants = useMemo(() => {
      if (selectedVendor?.variants?.length > 0) {
        return selectedVendor.variants;
      }
      return [];
    }, [selectedVendor]);

    return (
      <>
        {/* Name + description */}
        <Box className="pd-a1">
          <Typography
            sx={{
              fontFamily: "'Syne',sans-serif",
              fontSize: { xs: 17, md: 21 },
              fontWeight: 800,
              color: T.navy,
              lineHeight: 1.25,
              mb: 0.75,
              letterSpacing: "-0.02em",
            }}
          >
            {product?.name}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: T.muted, lineHeight: 1.65 }}>
            {product?.description}
          </Typography>
        </Box>

        <Divider />

        {/* Price Display */}
        {/* Enhanced Price Display */}
        <Box
          className="pd-a2"
          sx={{
            bgcolor: `linear-gradient(135deg, ${T.blueSoft}20, transparent)`,
            // p: 2,
            borderRadius: 3,
            // mt: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              {/* <Label>Price</Label> */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.75,
                  // mt: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: 26, fontWeight: 600, color: T.navy }}
                >
                  {/* ₹{Number(currentPrice).toFixed(2)} */}₹
                  {Number(currentPrice) && !isNaN(Number(currentPrice))
                    ? Number(currentPrice).toFixed(2)
                    : "0.00"}
                </Typography>
                {currentMrp > currentPrice && currentPrice > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: 16,
                        color: T.muted,
                        textDecoration: "line-through",
                      }}
                    >
                      ₹{currentMrp ? Number(currentMrp).toFixed(2) : "0.00"}
                    </Typography>
                    <Box
                      sx={{
                        // bgcolor: T.success + "20",
                        // px: 2,
                        // py: 1,
                        borderRadius: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 15, fontWeight: 800, color: T.success }}
                      >
                        ₹{" "}
                        {(Number(currentMrp) - Number(currentPrice)).toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* <Divider /> */}
        {/* Variants for Selected Vendor */}
        {currentVariants.length > 0 && (
          <>
            {/* <Divider /> */}
            <VariantSelector
              variants={currentVariants}
              selectedVariant={selectedVendorVariant}
              onVariantSelect={onVariantSelect}
            />
          </>
        )}

        {/* Flag chips */}
        {(product?.consentRequired || product?.isPrescriptionRequired) && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {product.consentRequired && (
              <Chip
                label="Consent required"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontSize: 11, borderRadius: 99, fontWeight: 700 }}
              />
            )}
            {product.isPrescriptionRequired && (
              <Chip
                label="Prescription required"
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontSize: 11, borderRadius: 99, fontWeight: 700 }}
              />
            )}
          </Box>
        )}

        {/* Spacer to push CTA to bottom */}
        {/* <Box sx={{ flex: 1 }} /> */}

        {/* CTA */}
        <Box
          className="pd-a5"
          sx={{
            display: "flex",
            gap: 1.5,
            position: "sticky",
            bottom: 0,
            bgcolor: T.white,
            pt: 1.5,
            pb: 0.5,
            borderTop: `1px solid ${T.border}`,
            // mt: 1,
          }}
        >
          <Button
            variant="contained"
            startIcon={
              addedToCart ? (
                <CheckCircle sx={{ fontSize: 18 }} />
              ) : isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <ShoppingCartOutlined sx={{ fontSize: 18 }} />
              )
            }
            onClick={onAddToCart}
            disabled={!selectedVendor?.isAvailableNow || isLoading}
            sx={{
              flex: 1,
              py: 1.4,
              borderRadius: 2.5,
              fontSize: 13.5,
              fontWeight: 800,
              textTransform: "none",
              boxShadow: "none",
              background: addedToCart ? "#00c896" : T.gradBlue,
              "&:hover": {
                background: addedToCart
                  ? "#00a87e"
                  : "linear-gradient(135deg,#3370ff,#1a5aff)",
              },
              "&.Mui-disabled": {
                background: T.border,
                color: T.muted,
              },
            }}
          >
            {addedToCart
              ? "Added to cart ✓"
              : isLoading
                ? "Adding..."
                : selectedVendor?.isAvailableNow
                  ? "Add to cart"
                  : "Out of Stock"}
          </Button>

          <IconButton
            onClick={onFavoriteToggle}
            sx={{
              border: `1.5px solid ${isFavorite ? "#fca5a5" : T.border}`,
              borderRadius: 2.5,
              px: 2,
              bgcolor: isFavorite ? "#fff5f5" : T.surface,
            }}
          >
            {isFavorite ? (
              <FavoriteOutlined sx={{ color: "#e53935", fontSize: 20 }} />
            ) : (
              <FavoriteBorderOutlined sx={{ color: T.muted, fontSize: 20 }} />
            )}
          </IconButton>
        </Box>
        {/* Seller Selection Section - Modular */}
        {vendors?.length > 0 && (
          <SellerSection
            vendors={vendors}
            selectedVendor={selectedVendor}
            onVendorSelect={onVendorSelect}
            requiredPostalCode={postalCode}
          />
        )}

        {/* Specifications */}
        {specs.length > 0 && (
          <>
            {/* <Divider /> */}
            <Box className="pd-a2">
              <Label>Specifications</Label>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))",
                  gap: 1,
                  mt: 1,
                }}
              >
                {specs.map((attr, i) => (
                  <SpecCard key={i} label={attr?.name} value={attr?.value} />
                ))}
                <SpecCard
                  label="Base unit"
                  value={`${product?.baseUomName} (${product?.baseUomSymbol})`}
                />
              </Box>
            </Box>
          </>
        )}

        {/* <Divider /> */}
      </>
    );
  },
);

// Main ProductDetails Component
const ProductDetails = ({ productUuid, open, onClose }) => {
  const [product, setProduct] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedVendorVariant, setSelectedVendorVariant] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const postCode = useSelector((state) => state.orders?.postalcode);

  console.log("ProductDetails - postCode from Redux:", postCode);
  console.log("selectedVendor in ProductDetails:", selectedVendor);

  const [isAddingToCart, setIsAddingToCart] = useState(false); // Added this state
  const [cartError, setCartError] = useState(null); // Added this state

  // Check if user is authenticated (you need to get this from your Redux store)
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  console.log("User authenticated:", isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  // Prepare cart item data from current selection
  const prepareCartData = useCallback(() => {
    if (!selectedVendor) return null;

    // Get the active variant (if any)
    const activeVariant =
      selectedVendorVariant ||
      (selectedVendor.variants && selectedVendor.variants[0]) ||
      null;

    // Get images
    const primaryImage =
      product?.images?.find((img) => img.isPrimary) || product?.images?.[0];

    // Calculate all required fields
    let finalPrice = 0;
    let salePrice = 0;
    let mrp = 0;
    let originalPrice = 0;
    let taxableAmount = 0;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;
    let percentageCgst = 0;
    let percentageSgst = 0;
    let percentageIgst = 0;
    let vendorCgst = 0;
    let vendorSgst = 0;
    let vendorIgst = 0;
    let vendorTaxableAmount = 0;
    let priceBreakdown = {};
    let hsnCode = selectedVendor.hsnCode || 0;
    let marginApplied = selectedVendor.marginApplied || 0;
    let marginType = selectedVendor.marginType || "percentage";
    let isMrp = selectedVendor.isMrp || false;

    if (activeVariant) {
      // Using variant data
      finalPrice =
        Number(activeVariant.finalPrice) ||
        Number(activeVariant.variantSalePrice) ||
        0;
      salePrice = Number(activeVariant.variantSalePrice) || 0;
      mrp = activeVariant.variantMrp || "0";
      originalPrice = Number(activeVariant.originalPrice) || 0;
      taxableAmount = Number(activeVariant.taxableAmount) || 0;
      cgst = Number(activeVariant.cgst) || 0;
      sgst = Number(activeVariant.sgst) || 0;
      igst = Number(activeVariant.igst) || 0;
      percentageCgst = Number(activeVariant.percentageCgst) || 0;
      percentageSgst = Number(activeVariant.percentageSgst) || 0;
      percentageIgst = Number(activeVariant.percentageIgst) || 0;
      vendorCgst = Number(activeVariant.vendorCgst) || 0;
      vendorSgst = Number(activeVariant.vendorSgst) || 0;
      vendorIgst = Number(activeVariant.vendorIgst) || 0;
      vendorTaxableAmount = Number(activeVariant.vendorTaxableAmount) || 0;
      priceBreakdown = activeVariant.priceBreakdown || {
        basePrice: 0,
        marginAmount: 0,
        taxAmount: 0,
        totalTax: 0,
      };
    } else {
      // Using vendor data (no variant)
      finalPrice =
        Number(selectedVendor.finalPrice) ||
        Number(selectedVendor.salePrice) ||
        0;
      salePrice = Number(selectedVendor.salePrice) || 0;
      mrp = selectedVendor.mrp || "0";
      originalPrice = Number(selectedVendor.originalPrice) || 0;
      taxableAmount = Number(selectedVendor.taxableAmount) || 0;
      cgst = Number(selectedVendor.cgst) || 0;
      sgst = Number(selectedVendor.sgst) || 0;
      igst = Number(selectedVendor.igst) || 0;
      percentageCgst = Number(selectedVendor.percentageCgst) || 0;
      percentageSgst = Number(selectedVendor.percentageSgst) || 0;
      percentageIgst = Number(selectedVendor.percentageIgst) || 0;
      vendorCgst = Number(selectedVendor.vendorCgst) || 0;
      vendorSgst = Number(selectedVendor.vendorSgst) || 0;
      vendorIgst = Number(selectedVendor.vendorIgst) || 0;
      vendorTaxableAmount = Number(selectedVendor.vendorTaxableAmount) || 0;
      priceBreakdown = selectedVendor.priceBreakdown || {
        basePrice: 0,
        marginAmount: 0,
        taxAmount: 0,
        totalTax: 0,
      };
    }

    // Prepare cart payload
    const cartPayload = {
      // Basic product info
      productId: product.uuid,
      productName: product.name,
      quantity: 1,

      // Vendor info
      vendorId: selectedVendor.vendorId,
      vendorName: selectedVendor.vendorName,
      vendorProductId: selectedVendor.vendorProductId,
      vendorPostalCode: selectedVendor.postalCode || "560092",

      // Pricing
      finalPrice: finalPrice,
      salePrice: salePrice,
      mrp: String(mrp),
      originalPrice: originalPrice,
      taxableAmount: taxableAmount,
      priceAtTime: finalPrice, // Price at time of adding to cart

      // Tax details
      cgst: cgst,
      sgst: sgst,
      igst: igst,
      percentageCgst: percentageCgst,
      percentageSgst: percentageSgst,
      percentageIgst: percentageIgst,
      vendorCgst: vendorCgst,
      vendorSgst: vendorSgst,
      vendorIgst: vendorIgst,
      vendorTaxableAmount: vendorTaxableAmount,

      // Price breakdown
      priceBreakdown: priceBreakdown,

      // Other details
      hsnCode: hsnCode,
      marginApplied: marginApplied,
      marginType: marginType,
      isMrp: isMrp,
      hasVariants: product.hasVariants || false,
      consentRequired: product.consentRequired || false,
      offeringType: "product", // or "menu_item" based on your product type

      // Image
      imageUrl: primaryImage?.url || "",

      // Prescription files (if any)
      prescriptionFiles: [],

      // Variant info (if variant exists)
      ...(activeVariant && {
        variantId: String(
          activeVariant.masterVariantUuid ||
            activeVariant.masterProductVariantId,
        ),
        variant: {
          id: String(
            activeVariant.masterVariantUuid ||
              activeVariant.masterProductVariantId,
          ),
          name: activeVariant.variantName,
          salePrice: salePrice,
          vendorVariantId: activeVariant.vendorVariantId,
        },
      }),
    };

    return cartPayload;
  }, [product, selectedVendor, selectedVendorVariant]);

  // Handle add to cart with authentication check
  const handleAddToCart = useCallback(async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Open login modal
      const { openSignIn } =
        await import("../../../components/common/SignIn/signin");

      openSignIn({
        onSuccess: async (userData) => {
          console.log("Login successful:", userData);
          // After successful login, add to cart
          await addToCartAfterLogin();
        },
        onClose: () => {
          console.log("Login modal closed");
        },
      });
      return;
    }

    // If already authenticated, add to cart directly
    await addToCartAfterLogin();
  }, [isAuthenticated, prepareCartData]);

  // Function to add item to cart
  const addToCartAfterLogin = useCallback(async () => {
    const cartData = prepareCartData();

    if (!cartData) {
      console.error("No cart data prepared");
      setError("Unable to add item to cart. Please try again.");
      return;
    }

    console.log("Adding to cart with payload:", cartData);

    // setIsLoading(true);
    setError(null);

    try {
      const response = await AddtoCart(cartData);

      if (response.data.success) {
        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 2200);

        // Optional: Show success message
        // You can also update cart count in Redux/Context
      } else {
        setError(response.data.message || "Failed to add item to cart");
      }
    } catch (err) {
      console.error("Error adding to cart:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to add item to cart",
      );
    } finally {
      // setIsLoading(false);
    }
  }, [prepareCartData]);

  const loadedUuidRef = useRef(null);

  useEffect(() => {
    injectStyles();
  }, []);

  // Fetch product details (only once when component opens)
  useEffect(() => {
    if (!open || !productUuid) return;

    // Check if postal code is available
    if (!postCode) {
      console.log("Waiting for postal code...");
      // Don't fetch yet - wait for postal code
      return;
    }

    if (productUuid !== loadedUuidRef.current) {
      loadedUuidRef.current = productUuid;
      setProduct(null);
      setVendors([]);
      setSelectedVendor(null);
      setSelectedVendorVariant(null);
      setError(null);
      setActiveImage(null);
      setActiveIdx(0);
      setIsZoomed(false);
      setAddedToCart(false);
      fetchVendorsAndProduct();
    }
  }, [productUuid, open, postCode]); // Add postCode to dependencies

  // Fetch vendors data (only once)
  const fetchVendorsAndProduct = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch both product details and vendors in parallel
      const [productRes, vendorsRes] = await Promise.all([
        productDetails(productUuid),
        MultipleVendorforProduct(productUuid, postCode),
      ]);

      // Process product data
      const productOk = productRes?.data?.success || productRes?.success;
      if (productOk) {
        const productData =
          productRes?.data?.data?.masterProduct ||
          productRes?.data?.masterProduct;
        setProduct(productData);
        const primary = productData.images?.find((i) => i.isPrimary);
        const firstUrl = primary?.url || productData.images?.[0]?.url || null;
        setActiveImage(firstUrl);
        setActiveIdx(
          productData.images?.findIndex((i) => i.url === firstUrl) ?? 0,
        );
      }

      // Process vendors data
      const vendorsOk = vendorsRes?.data?.success || vendorsRes?.success;
      if (vendorsOk) {
        const vendorsData =
          vendorsRes?.data?.data?.vendors || vendorsRes?.data?.vendors || [];
        // Filter vendors that have valid pricing
        const validVendors = vendorsData.filter(
          (v) => v.salePrice > 0 || (v.variants && v.variants.length > 0),
        );
        setVendors(validVendors);

        // Auto-select first available vendor
        if (validVendors.length > 0) {
          const firstVendor = validVendors[0];
          setSelectedVendor(firstVendor);

          // Auto-select first variant if available
          if (firstVendor.variants && firstVendor.variants.length > 0) {
            setSelectedVendorVariant(firstVendor.variants[0]);
          } else {
            setSelectedVendorVariant(null);
          }
        }
      } else {
        setError("Failed to load vendor information");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Handle vendor selection (no API call - just state update)
  const handleVendorSelect = useCallback((vendor) => {
    setSelectedVendor(vendor);
    // Reset variant selection when vendor changes
    if (vendor.variants && vendor.variants.length > 0) {
      setSelectedVendorVariant(vendor.variants[0]);
    } else {
      setSelectedVendorVariant(null);
    }
  }, []);

  // Handle variant selection for current vendor (no API call)
  const handleVariantSelect = useCallback((variant) => {
    setSelectedVendorVariant(variant);
  }, []);

  // Handle image switching
  const switchImage = useCallback((url, idx) => {
    setActiveImage(url);
    setActiveIdx(idx);
    setIsZoomed(false);
  }, []);

  // Handle zoom
  const handleMouseMove = useCallback(
    (e) => {
      if (!isZoomed) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomPosition({
        x: Math.min(Math.max(x, 0), 100),
        y: Math.min(Math.max(y, 0), 100),
      });
    },
    [isZoomed],
  );

  const handleZoomToggle = useCallback(() => {
    setIsZoomed((z) => !z);
  }, []);

  const handleFavoriteToggle = useCallback(() => {
    setIsFavorite((f) => !f);
  }, []);

  if (!open) return null;

  const Root = ({ children }) => (
    <Box
      className="pd-root"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: T.white,
        overflow: "hidden",
      }}
    >
      {children}
    </Box>
  );

  // Loading state
  if (loading)
    return (
      <Root>
        <TopBar onClose={onClose} label="Loading…" />
        <Box
          sx={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}
        >
          <Box sx={leftPanelSx}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                height: "100%",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.2,
                  flexShrink: 0,
                }}
              >
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    width={70}
                    height={70}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Box>
              <Skeleton
                variant="rounded"
                sx={{
                  flex: 1,
                  height: 350,
                  borderRadius: 3,
                  background:
                    "linear-gradient(90deg,#eff1f7 25%,#fafbfd 50%,#eff1f7 75%)",
                  backgroundSize: "600px 100%",
                  animation: "pd-shimmer 1.4s infinite linear",
                }}
              />
            </Box>
          </Box>
          <Box sx={rightPanelSx}>
            <Skeleton width="65%" height={30} sx={{ mb: 1 }} />
            <Skeleton width="40%" height={16} sx={{ mb: 3 }} />
            <Skeleton
              variant="rounded"
              height={200}
              sx={{ borderRadius: 2.5, mb: 2 }}
            />
            <Skeleton
              variant="rounded"
              height={48}
              sx={{ borderRadius: 2.5 }}
            />
          </Box>
        </Box>
      </Root>
    );

  // Error state
  if (error)
    return (
      <Root>
        <TopBar onClose={onClose} label="Product Details" />
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            onClick={fetchVendorsAndProduct}
            startIcon={<Replay />}
            sx={{ borderRadius: 99, textTransform: "none", fontWeight: 700 }}
          >
            Try Again
          </Button>
        </Box>
      </Root>
    );

  // No product state
  if (!product) return null;

  // Main render
  return (
    <Root>
      <TopBar onClose={onClose} label={product.categoryName || "Details"} />

      <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* LEFT PANEL */}
        <Box sx={leftPanelSx}>
          <ImageGallery
            images={product.images || []}
            activeIdx={activeIdx}
            activeImage={activeImage}
            isZoomed={isZoomed}
            zoomPosition={zoomPosition}
            isFavorite={isFavorite}
            onImageSwitch={switchImage}
            onZoomToggle={handleZoomToggle}
            onMouseMove={handleMouseMove}
            onFavoriteToggle={handleFavoriteToggle}
          />

          {/* Brand + category */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            {product.attributes?.find((a) => a.name === "Brand")?.value && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.4,
                  py: 0.45,
                  borderRadius: 99,
                  bgcolor: T.blueSoft,
                  color: T.blue,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                }}
              >
                <VerifiedOutlined sx={{ fontSize: 12 }} />
                {product.attributes.find((a) => a.name === "Brand").value}
              </Box>
            )}
            {product.categoryPath && (
              <Typography sx={{ fontSize: 11, color: T.muted }}>
                {product.categoryPath}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={rightPanelSx}>
          <ProductInfoPanel
            product={product}
            vendors={vendors}
            selectedVendor={selectedVendor}
            selectedVendorVariant={selectedVendorVariant}
            onVendorSelect={handleVendorSelect}
            onVariantSelect={handleVariantSelect}
            isFavorite={isFavorite}
            onFavoriteToggle={handleFavoriteToggle}
            addedToCart={addedToCart}
            onAddToCart={handleAddToCart}
            postalCode={postCode} // Add this line
            isLoading={isAddingToCart}
          />
        </Box>
      </Box>
    </Root>
  );
};

export default ProductDetails;
